import { useEffect, useState } from "react";
import { Users, Percent, DollarSign, Star } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { gratuityColor } from "@/lib/fields";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();
const fmtMonth = (k) => {
  const [y, m] = String(k).split("-");
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "short", year: "2-digit" });
};

const Stat = ({ icon: Icon, label, value, accent, testid, valueClass }) => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={testid}>
    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-800" style={{ color: accent }}>
      <Icon className="h-4 w-4" />
    </div>
    <div className={`font-display text-2xl font-800 tracking-tight ${valueClass || "text-zinc-50"}`}>{value}</div>
    <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
  </div>
);

export const SeasonInsights = ({ chartOnly = false }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.stats().then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const trend = s.trend || [];

  return (
    <div className="mb-6 space-y-4" data-testid="season-insights">
      {!chartOnly && (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat icon={Users} label="Total Tours" value={s.total_guests} accent="#E0575B" testid="stat-total-tours" />
        <Stat icon={Percent} label="Avg Tip %" value={`${s.avg_tip_percent || 0}%`} accent="#E0575B" valueClass={gratuityColor(s.avg_tip_percent)} testid="stat-avg-tip" />
        <Stat icon={DollarSign} label="Total Retail" value={money(s.total_revenue)} accent="#E0575B" testid="stat-total-retail" />
        <Stat icon={Star} label="Avg Rating" value={s.avg_rating || 0} accent="#E0575B" testid="stat-avg-rating" />
      </div>
      )}

      {trend.length > 1 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="tips-trend-chart">
          <h3 className="mb-3 font-display text-sm font-700 tracking-tight text-zinc-200">Tips &amp; Ratings Over Time</h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trend} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,148,160,0.15)" vertical={false} />
                <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fill: "#71717a", fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis yAxisId="left" tick={{ fill: "#71717a", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + "k" : v}`} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tick={{ fill: "#71717a", fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 10, fontSize: 12 }}
                  labelFormatter={fmtMonth}
                  formatter={(val, name) => name === "avg_gratuity" ? [money(val), "Avg tip"] : [val, "Avg rating"]}
                />
                <Bar yAxisId="left" dataKey="avg_gratuity" name="avg_gratuity" fill="#34d399" radius={[4, 4, 0, 0]} maxBarSize={26} />
                <Line yAxisId="right" type="monotone" dataKey="avg_rating" name="avg_rating" stroke="#fbbf24" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex items-center gap-4 text-xs text-zinc-400">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Avg tip</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" /> Avg rating</span>
          </div>
        </div>
      )}
    </div>
  );
};
